<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from '@composables/use-store'
import localSave from '@utils/local-save'

const TYPE_CLASSES = {
    warning: 'panel-warning',
    danger: 'panel-danger',
    success: 'panel-success',
}

const ACTIVE_DURATION = 8000 // ms
const ANIMATION_DURATION = 500 // ms
const INITIAL_COUNTDOWN = 60 // secs

const store = useStore()

let timeout = null

const show = ref(false)
const countdownDuration = ref(INITIAL_COUNTDOWN)

const notificationType = computed(() => store.state.appearance.notificationType)
const hasReloadButton = computed(() => store.state.appearance.hasReload)
const isPersistentNotification = computed(() => store.state.appearance.isPersistentNotification)
const forceUpdate = computed(() => store.state.appearance.forceUpdate)
const notificationMessage = computed(() => store.state.appearance.notificationMessage)

const typeClass = computed(() => TYPE_CLASSES[notificationType.value])

function close() {
    show.value = false
    setTimeout(() => {
        store.dispatch('appearance/closeNotification')
    }, ANIMATION_DURATION)
}

function reloadPage() {
    localSave.save('localCodeVersion', localSave.get('actualCodeVersion'))
    window.location.reload(true)
}

watch(countdownDuration, () => {
    countdownDuration.value === 0 && reloadPage()
})

onMounted(() => {
    setTimeout(() => {
        show.value = true
    }, 0)
    timeout = setTimeout(() => {
        !isPersistentNotification.value && close()
    }, ACTIVE_DURATION)
    if (forceUpdate.value) {
        setInterval(() => {
            countdownDuration.value--
        }, 1000)
    }
})

onBeforeUnmount(() => {
    clearTimeout(timeout)
})
</script>

<template>
    <div class="notification-dialog">
        <div :class="['panel', typeClass, show && 'panel-active']">
            <div class="main">
                <span
                    v-safe-html="notificationMessage"
                    class="message"
                />
                <span
                    v-if="forceUpdate"
                    class="countdown"
                >&nbsp;{{ countdownDuration }}s</span>
                <div
                    v-if="hasReloadButton"
                    class="reload-button"
                >
                    <BaseButton
                        :modifiers="{ noMargins: true }"
                        button-type="white"
                        @click="reloadPage()"
                    >Reload</BaseButton>
                </div>
            </div>
            <div
                class="icon-container"
                @click="close()"
            >
                <BaseIcon
                    class="icon"
                    name="close"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.panel {
    position: fixed;
    top: 0;
    left: 0;
    z-index: $layer-message-z-index;
    display: flex;
    width: 100%;
    min-height: 60px;
    color: $c-white;
    background-color: $c-black;
    border: 1px solid $c-shark;
    opacity: 0;
    transform: translateY(-100%);
    transition: all 0.5s ease-in-out;

    &.panel-active {
        opacity: 1;
        transform: translateY(0);
    }

    @extend %contentSubtitleM;

    &.panel-warning {
        background: $c-message-warning;
    }
    &.panel-danger {
        background: $c-message-danger;
    }
    &.panel-success {
        background: $c-message-success;
    }
}

.main {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: $s-xs;

    // The message is injected by v-safe-html, so its markup carries no scope id.
    ::v-deep a {
        font-size: 17px;
        font-weight: bold;
        color: $c-white;
    }
}

.message {
    font-size: 15px;
}

.icon {
    position: absolute;
    right: $s-xxl;
    display: none;

    @include square(30px);

    fill: $c-white;
}

.icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.reload-button {
    margin-left: $s-xl;
}

.countdown {
    font-size: 18px;
    font-weight: bold;
}

@include desktop {
    .icon {
        display: inline-block;
    }
}
</style>
